import { Button } from '@workspace/ui/components/button';
import { Input } from '@workspace/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@workspace/ui/components/table';
import { cn } from '@workspace/ui/lib/utils';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  SendHorizonal,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import type { TableFormArgs } from '~/hooks/use-chat/types';
import { fetchJson } from '../../../utils/fetch-json-v2';
import type { ToolProps } from './types';

export function TableForm({ handleAction, toolCall, isActive }: ToolProps) {
  const tableArgs = toolCall.args as TableFormArgs;
  const [rows, setRows] = useState(tableArgs.rows);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [formData, setFormData] = useState<
    Record<string, Record<string, string | boolean>>
  >(() => {
    const initialData: Record<string, Record<string, string | boolean>> = {};

    tableArgs.rows.forEach((row) => {
      initialData[row.id] = {};
      row.cells.forEach((cell) => {
        if (cell.value !== undefined) {
          initialData[row.id][cell.column_id] = cell.value;
        }
      });
    });

    return initialData;
  });

  const [errors, setErrors] = useState<Record<string, Record<string, string>>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalPages = Math.ceil(rows.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRows = rows.slice(startIndex, endIndex);

  const handleInputChange = (
    rowId: string,
    columnId: string,
    value: string | boolean,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        [columnId]: value,
      },
    }));

    if (errors[rowId]?.[columnId]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[rowId]) {
          delete newErrors[rowId][columnId];
          if (Object.keys(newErrors[rowId]).length === 0) {
            delete newErrors[rowId];
          }
        }
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, Record<string, string>> = {};

    rows.forEach((row) => {
      row.cells.forEach((cell) => {
        const column = tableArgs.columns.find(
          (col) => col.id === cell.column_id,
        );
        const isRequired = column?.required;

        if (isRequired) {
          const value = formData[row.id]?.[cell.column_id];
          const cellType = column?.type || 'text';

          if (cellType === 'checkbox') {
            if (!value) {
              if (!newErrors[row.id]) newErrors[row.id] = {};
              newErrors[row.id][cell.column_id] = 'Este campo es requerido';
            }
          } else {
            if (!value || (typeof value === 'string' && value.trim() === '')) {
              if (!newErrors[row.id]) newErrors[row.id] = {};
              newErrors[row.id][cell.column_id] = 'Este campo es requerido';
            }
          }
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const arrayData = rows.map((row) => {
        const rowData: Record<string, string | boolean> = {
          id: row.id,
        };

        row.cells.forEach((cell) => {
          const value = formData[row.id]?.[cell.column_id];
          if (value !== undefined) {
            rowData[cell.column_id] = value;
          }
        });

        return rowData;
      });

      const jsonString = JSON.stringify(arrayData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const fileName = `${tableArgs.field_name}${tableArgs.field_type}`;
      const file = new File([blob], fileName, { type: 'application/json' });

      const formDataForUpload = new FormData();
      formDataForUpload.append('file', file);

      const { data: fileData } = await fetchJson<{ urn: string }>('/files', {
        method: 'POST',
        body: formDataForUpload,
      });

      const response = {
        [tableArgs.field_name]: fileData?.urn || '',
      };
      await handleAction(toolCall, JSON.stringify(response));
    } catch (error) {
      console.error('Error al enviar formulario:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addRow = () => {
    const newRowId = `row_${Date.now()}`;
    const newRow = {
      id: newRowId,
      cells: tableArgs.columns.map((column) => ({
        column_id: column.id,
        type: column.type || 'text',
        placeholder: `Enter ${column.title}`,
        value: '',
        required: column.required,
        options: column.options,
      })),
    };

    setRows((prev) => [...prev, newRow]);
    setFormData((prev) => ({ ...prev, [newRowId]: {} }));

    // Navigate to the last page if the new row would be on a new page
    const newTotalPages = Math.ceil((rows.length + 1) / itemsPerPage);
    if (newTotalPages > totalPages) {
      setCurrentPage(newTotalPages);
    }
  };

  const removeRow = (rowId: string) => {
    setRows((prev) => prev.filter((row) => row.id !== rowId));
    setFormData((prev) => {
      const newData = { ...prev };
      delete newData[rowId];
      return newData;
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[rowId];
      return newErrors;
    });

    const newTotalPages = Math.ceil((rows.length - 1) / itemsPerPage);
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
    }
  };

  const renderCell = (
    row: TableFormArgs['rows'][number],
    cell: TableFormArgs['rows'][number]['cells'][number],
  ) => {
    const value = formData[row.id]?.[cell.column_id] || '';
    const hasError = errors[row.id]?.[cell.column_id];
    const column = tableArgs.columns.find((col) => col.id === cell.column_id);
    const cellType = column?.type || 'text';
    const cellOptions = column?.options;

    switch (cellType) {
      case 'text':
      case 'number':
      case 'email':
      case 'phone':
        return (
          <div className="space-y-1">
            <Input
              type={cellType === 'phone' ? 'tel' : cellType}
              placeholder={cell.placeholder}
              value={value as string}
              onChange={(e) =>
                handleInputChange(row.id, cell.column_id, e.target.value)
              }
              className={cn('dark:bg-background h-8 text-sm', {
                'border-red-500': hasError,
              })}
              required={column?.required}
              disabled={!isActive}
            />
            {hasError && <p className="text-xs text-red-500">{hasError}</p>}
          </div>
        );

      case 'select':
        return (
          <div className="space-y-1">
            <Select
              value={value as string}
              onValueChange={(newValue) =>
                handleInputChange(row.id, cell.column_id, newValue)
              }
              required={column?.required}
            >
              <SelectTrigger
                className={cn(
                  'w-full dark:bg-background border-[#27272a] text-white text-sm h-8',
                  {
                    'border-red-500': hasError,
                  },
                )}
                disabled={!isActive}
              >
                <SelectValue placeholder={cell.placeholder} />
              </SelectTrigger>
              <SelectContent className="bg-background border-gray-700">
                {cellOptions?.map(
                  (option: { value: string; label: string }) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      className="text-white hover:bg-gray-800"
                    >
                      {option.label}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
            {hasError && <p className="text-xs text-red-500">{hasError}</p>}
          </div>
        );

      case 'date':
        return (
          <div className="space-y-1">
            <Input
              type="date"
              value={value as string}
              onChange={(e) =>
                handleInputChange(row.id, cell.column_id, e.target.value)
              }
              className={cn('dark:bg-background h-8 text-sm', {
                'border-red-500': hasError,
              })}
              required={column?.required}
              disabled={!isActive}
            />
            {hasError && <p className="text-xs text-red-500">{hasError}</p>}
          </div>
        );

      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={value as boolean}
              onChange={(e) =>
                handleInputChange(row.id, cell.column_id, e.target.checked)
              }
              className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${hasError ? 'border-red-500' : ''}`}
              required={column?.required}
              disabled={!isActive}
            />
            {hasError && <p className="text-xs text-red-500">{hasError}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-0">
      <div className="mb-6">
        <h2 className="text-lg font-bold text-white mb-2">{tableArgs.title}</h2>
        <p className="text-sm text-muted-foreground">{tableArgs.content}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="w-full border border-border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                {tableArgs.columns.map((column) => (
                  <TableHead
                    key={column.id}
                    className="text-white font-medium bg-muted/20 whitespace-nowrap"
                  >
                    {column.title}
                  </TableHead>
                ))}
                {tableArgs.row_controls?.includes('remove') && (
                  <TableHead className="text-white font-medium bg-muted/20 w-16">
                    Acción
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentRows.map((row) => (
                <TableRow key={row.id} className="border-border">
                  {tableArgs.columns.map((column) => {
                    const cell = row.cells.find(
                      (c) => c.column_id === column.id,
                    );
                    return (
                      <TableCell
                        key={`${row.id}-${column.id}`}
                        className="p-3 whitespace-nowrap"
                      >
                        {cell ? renderCell(row, cell) : null}
                      </TableCell>
                    );
                  })}
                  {tableArgs.row_controls?.includes('remove') && (
                    <TableCell className="p-3">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => removeRow(row.id)}
                        disabled={!isActive || rows.length <= 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                Página {currentPage} de {totalPages} ({rows.length} filas)
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            {tableArgs.row_controls?.includes('add') && (
              <Button
                type="button"
                variant="outline"
                onClick={addRow}
                disabled={!isActive}
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar fila
              </Button>
            )}
          </div>
        )}

        {totalPages <= 1 && tableArgs.row_controls?.includes('add') && (
          <div className="flex justify-start">
            <Button
              type="button"
              variant="outline"
              onClick={addRow}
              disabled={!isActive}
            >
              <Plus className="h-4 w-4 mr-2" />
              Agregar fila
            </Button>
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting || !isActive}>
            {isSubmitting ? 'Enviando...' : 'Confirmar configuración'}
            <SendHorizonal className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </form>
    </div>
  );
}
